import { enemyByKind } from "./enemy-defs.js";
/**
 * Examine flavor text — client-side only, shown as system lines in chat.
 * Never a server round trip; the server is untouched by these strings.
 */
import { consumableById, EQUIP_SLOTS, itemByKind } from "@pd/shared";
import type { Observation, ObservedPlayer } from "@pd/shared";
import type { WorldObject } from "./actions.js";
import { keyLetter } from "./actions.js";

export function examineObject(obs: Observation, obj: WorldObject): string {
  switch (obj.kind) {
    case "wizardOrb": return "Click from anywhere in this room to strike the wizard. Each hit in the wave grows stronger; the fifth kills him. The ring shows the shared deadline.";
    case "chasmTile":
      return obj.state === 0 ? "An untested crossing tile. Inspect it from beside it to reveal safe footing or a chasm. Watch for passing shimmers; stepping onto a chasm is fatal." : obj.state === 1 ? "An open chasm. Falling kills you; carried gatestones and Phoenix return to the last safe tile you stood on." : "Confirmed safe footing. The party shares this discovery.";
    case "iceTile":
      return obj.blocked ? "A fixed boulder. You stop before it; diagonal slides cannot cut its corners." : obj.pad >= 0 ? `Pressure pad ${obj.pad + 1}${obj.active ? " is already active" : " lights when you land on it"}. Pads stop your slide and stay lit for the party. Activate all four to open the onward doors.` : "Slippery ice across the whole room. A normal floor tile just inside each door interrupts a slide; pads, boulders and the room wall stop you too. Click a direction to slide straight, including diagonally. Slides use your normal movement speed; gatestone teleports cancel a slide.";
    case "puzzleTile":
      return obj.solved ? "All tiles match. The puzzle is solved." : `Imbue flips this tile and its four neighbours. Force flips only this tile and costs ${obj.forceDamage} HP. Make every tile the same colour.`;
    case "key":
      return `The mark is "${keyLetter(obj.id)}". Find the door that matches.`;
    case "stone":
      return obj.stoneKind === "group"
        ? "The party's gatestone. Anyone can carry it; everyone can teleport to it."
        : "Your own way back. Teleporting uses this stone up; summon it again when you need it.";
    case "door":
      if (obj.open) return "The way ahead is open.";
      if (obj.puzzleLocked && obj.puzzleKind === "orb-wizard") return "Defeat the wizard in this room to open this door. Burst his orbs for increasing damage.";
      if (obj.puzzleLocked && obj.puzzleKind === "chasm-crossing") return "Cross the hidden path to an onward doorway to open this door.";
      if (obj.puzzleLocked) return obj.puzzleKind === "icy-pressure-pads" ? "Activate all four pressure pads to open this door." : "Make every tile the same colour to open this door.";
      if (obj.guarded)
        return "A guardian ward seals this door. Clear the room beyond and the ward will break. No key needed.";
      return `Locked tight. Look for a key marked "${keyLetter(obj.keyId)}".`;
    case "boss":
      // The boss object only exists once the boss is dead (actions.ts), so
      // this is always the open portal.
      return "Warm light spills from the exit. The floor ends when the party leaves through it.";
    case "returnPortal":
      return "A dim way back to the hub. You'll leave the party here and your pocket stays behind, but the gear you're wearing comes with you.";
    case "enemy": {
      // Per-family flavour (the shade's is its gameplay tell — it fights at
      // a distance); everything else keeps the generic line. The WorldObject
      // carries the enemy's id only; the kind rides the observation.
      const kind = obs.enemies.find((e) => e.id === obj.id)?.kind;
      if (kind !== undefined && enemyByKind(kind)?.family === "shade") {
        return "A shade keeps its distance and hurls shadow bolts. Close in before it gets another shot.";
      }
      return "It has seen you. Best decide who strikes first.";
    }
    case "corpse": {
      const name = enemyByKind(obj.npcKind)?.name?.toLowerCase() ?? "something's";
      return `A pile of ${name} bones. Already picked clean.`;
    }
    case "item": {
      const name =
        obj.cls === "equipment" ? itemByKind(obj.itemKind)?.name : consumableById(obj.itemKind)?.name;
      return obj.cls === "consumable"
        ? `${name ?? "A consumable"}. Pocket it now; use it when you need it.`
        : `${name ?? "An item"}. Pick it up to tuck it into your pocket.`;
    }
    case "sack":
      return "Someone stocked this sack before you arrived. Take what you need.";
  }
}

export function examinePlayer(p: ObservedPlayer): string {
  const keys = p.keys.length === 0 ? "no keys" : `${p.keys.length} key${p.keys.length === 1 ? "" : "s"}`;
  const stones = p.carried.length === 0 ? "no gatestones" : `${p.carried.length} gatestone${p.carried.length === 1 ? "" : "s"}`;
  // What they wear (worn gear, in slot order) — the equipment readout in
  // prose, party-visible like everything else in the observation.
  const worn: string[] = [];
  for (const slot of EQUIP_SLOTS) {
    const kind = p.equipment[slot];
    if (kind === null || kind === undefined) continue;
    const def = itemByKind(kind);
    if (def) worn.push(def.name.toLowerCase());
  }
  const gear = worn.length === 0 ? "nothing" : worn.join(", ");
  return `${p.name} carries ${keys} and ${stones}. Worn gear: ${gear}.`;
}
